import React, { useState, useEffect } from "react";
import { db, functions } from "../firebase";
import {
  collection,
  onSnapshot,
  addDoc,
  serverTimestamp
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import {
  SearchNormal1,
  Notification,
  Trash,
  ArrowLeft2,
  ArrowRight2,
  CloseCircle,
  Profile2User,
} from "iconsax-reactjs";
import "./UsersPanel.css";

const ITEMS_PER_PAGE = 12;

const ROLE_FILTERS = [
  { key: "all", label: "All" },
  { key: "tenant", label: "Tenants" },
  { key: "dealer", label: "Dealers" }
];

// Firestore Timestamp, millis, or ISO string to a Date (or null).
function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDate(value) {
  const date = toDate(value);
  return date ? date.toLocaleDateString([], { day: "numeric", month: "short", year: "numeric" }) : null;
}

function formatRelative(value) {
  const date = toDate(value);
  if (!date) return null;
  const diff = Date.now() - date.getTime();
  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;
  if (diff < 2 * minute) return "Just now";
  if (diff < hour) return `${Math.floor(diff / minute)} min ago`;
  if (diff < day) return `${Math.floor(diff / hour)} h ago`;
  if (diff < 7 * day) return `${Math.floor(diff / day)} d ago`;
  return formatDate(date);
}

// Page numbers to show: all of them when few, otherwise first, last and the
// neighbours of the current page with gaps between.
function pageWindow(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [...new Set([1, total, current - 1, current, current + 1])]
    .filter((p) => p >= 1 && p <= total)
    .sort((a, b) => a - b);
  const out = [];
  pages.forEach((p, i) => {
    if (i > 0 && p - pages[i - 1] > 1) out.push(`gap-${p}`);
    out.push(p);
  });
  return out;
}

export default function UsersPanel() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // Notification Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyBody, setNotifyBody] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [deletingUids, setDeletingUids] = useState([]);

  useEffect(() => {
    const usersCol = collection(db, "users");
    const unsubscribe = onSnapshot(usersCol, (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      // Sort in memory by createdAt descending
      list.sort((a, b) => {
        const timeA = a.createdAt?.seconds || a.createdAt?.toMillis?.() || 0;
        const timeB = b.createdAt?.seconds || b.createdAt?.toMillis?.() || 0;
        return timeB - timeA;
      });
      setUsers(list);
      setLoading(false);
    }, (error) => {
      console.error("Failed to fetch users:", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const openNotificationModal = (user) => {
    setSelectedUser(user);
    setNotifyTitle("");
    setNotifyBody("");
    setIsModalOpen(true);
  };

  const closeNotificationModal = () => {
    setIsModalOpen(false);
    setSelectedUser(null);
    setNotifyTitle("");
    setNotifyBody("");
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();
    if (!notifyTitle.trim() || !notifyBody.trim() || !selectedUser) {
      alert("Please fill in all fields.");
      return;
    }

    setIsSending(true);
    try {
      // Write to the user's personal notifications subcollection
      const userNotificationsCol = collection(db, "users", selectedUser.id, "notifications");
      await addDoc(userNotificationsCol, {
        title: notifyTitle.trim(),
        body: notifyBody.trim(),
        sentAt: serverTimestamp(),
        type: "individual"
      });

      // Call HTTPS Callable Cloud Function for actual Push Notification dispatch
      try {
        const sendNotificationFunc = httpsCallable(functions, "sendNotification");
        await sendNotificationFunc({
          title: notifyTitle.trim(),
          body: notifyBody.trim(),
          userId: selectedUser.id
        });
        console.log("Push notification dispatched via Cloud Function.");
      } catch (fnError) {
        console.warn("Cloud Function not deployed or failed, fallback to Firestore-only delivery:", fnError);
      }

      alert(`Notification successfully sent to ${selectedUser.name || selectedUser.phoneNumber || 'user'}.`);
      closeNotificationModal();
    } catch (err) {
      console.error("Error sending individual notification:", err);
      alert("Failed to send notification. Please try again.");
    } finally {
      setIsSending(false);
    }
  };

  const handleDeleteUser = async (user) => {
    const confirmMsg = `Are you sure you want to permanently delete user "${user.name || user.phoneNumber || user.email || 'Unnamed User'}"?\n\nThis will permanently delete:\n- Their Firebase Authentication account\n- Their Firestore user document and all subcollections\n- All properties they listed\n- All reviews they wrote (and recalculate ratings for reviewed properties)\n- Their FCM tokens\n- All chats they participated in\n\nThis action cannot be undone.`;

    if (window.confirm(confirmMsg)) {
      setDeletingUids((prev) => [...prev, user.id]);
      try {
        const deleteFunc = httpsCallable(functions, "deleteUserAccount");
        const res = await deleteFunc({ uid: user.id });
        alert(res.data?.message || "User successfully deleted.");
      } catch (err) {
        console.error("Error deleting user:", err);
        alert(`Failed to delete user: ${err.message}`);
      } finally {
        setDeletingUids((prev) => prev.filter((id) => id !== user.id));
      }
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    const parts = name.trim().split(/\s+/);
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0].substring(0, 2).toUpperCase();
  };

  const filteredUsers = users.filter((u) => {
    const searchLower = searchQuery.toLowerCase();
    const nameMatch = u.name?.toLowerCase().includes(searchLower);
    const phoneMatch = u.phoneNumber?.includes(searchQuery);
    const emailMatch = u.email?.toLowerCase().includes(searchLower);

    const matchesSearch = nameMatch || phoneMatch || emailMatch;

    // Treat missing role as 'tenant'
    const role = u.role || 'tenant';
    const matchesRole = roleFilter === "all" || role === roleFilter;

    return matchesSearch && matchesRole;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, filteredUsers.length);
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter]);

  const tenantCount = users.filter(u => (u.role || 'tenant') === "tenant").length;
  const dealerCount = users.filter(u => u.role === "dealer").length;

  const isFiltering = searchQuery.trim() !== "" || roleFilter !== "all";

  const renderSkeletonRows = () =>
    Array.from({ length: 6 }, (_, i) => (
      <tr key={`skeleton-${i}`} className="up-skeleton-row" aria-hidden="true">
        <td>
          <div className="up-user-cell">
            <span className="skeleton up-skel-avatar" />
            <span className="skeleton up-skel-line up-skel-name" />
          </div>
        </td>
        <td><span className="skeleton up-skel-line" /></td>
        <td><span className="skeleton up-skel-line up-skel-wide" /></td>
        <td><span className="skeleton up-skel-chip" /></td>
        <td><span className="skeleton up-skel-line" /></td>
        <td><span className="skeleton up-skel-line" /></td>
        <td />
      </tr>
    ));

  return (
    <div className="up-panel">
      <div className="stat-strip up-stats">
        <div className="stat">
          <span className="stat-label">Total users</span>
          <span className="stat-value">{loading ? "…" : users.length}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Tenants</span>
          <span className="stat-value">{loading ? "…" : tenantCount}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Dealers</span>
          <span className="stat-value">{loading ? "…" : dealerCount}</span>
        </div>
      </div>

      <div className="toolbar">
        <label className="search-field up-search">
          <SearchNormal1 size={16} variant="Linear" />
          <span className="sr-only">Search users</span>
          <input
            type="search"
            className="input"
            placeholder="Search by name, phone or email"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </label>
        <div className="segmented up-role-filter" role="group" aria-label="Filter by role">
          {ROLE_FILTERS.map((item) => (
            <button
              key={item.key}
              type="button"
              aria-pressed={roleFilter === item.key}
              onClick={() => setRoleFilter(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {!loading && paginatedUsers.length === 0 ? (
        <div className="card">
          <div className="empty-state">
            <span className="empty-state-icon"><Profile2User size={22} variant="Linear" /></span>
            {isFiltering ? (
              <>
                <h3>No users match</h3>
                <p>Try a different name, phone number or email, or switch the role filter back to All.</p>
              </>
            ) : (
              <>
                <h3>No users yet</h3>
                <p>People who sign up in the GharSetu app appear here. You can send each of them a notification.</p>
              </>
            )}
          </div>
        </div>
      ) : (
        <div className="table-wrap">
          <table className="table up-table">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Phone</th>
                <th scope="col">Email</th>
                <th scope="col">Role</th>
                <th scope="col">Joined</th>
                <th scope="col">Last active</th>
                <th scope="col"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? renderSkeletonRows()
                : paginatedUsers.map((u) => {
                  const role = u.role || 'tenant';
                  const displayName = u.name || 'Unnamed user';
                  const isDeleting = deletingUids.includes(u.id);
                  const joined = formatDate(u.createdAt);
                  const lastActive = formatRelative(u.lastSeen);
                  const lastSeenDate = toDate(u.lastSeen);
                  return (
                    <tr key={u.id}>
                      <td data-label="User">
                        <div className="up-user-cell">
                          <span className="avatar">
                            {u.avatarUrl ? (
                              <img src={u.avatarUrl} alt="" />
                            ) : (
                              getInitials(u.name)
                            )}
                          </span>
                          <span className="up-user-name" title={displayName}>{displayName}</span>
                        </div>
                      </td>
                      <td data-label="Phone" className="up-nowrap">
                        {u.phoneNumber || <span className="muted">No phone</span>}
                      </td>
                      <td data-label="Email" className="up-email">
                        {u.email ? <span title={u.email}>{u.email}</span> : <span className="muted">No email</span>}
                      </td>
                      <td data-label="Role">
                        <span className={`chip ${role === "dealer" ? "chip-primary" : ""}`}>
                          {role.charAt(0).toUpperCase() + role.slice(1)}
                        </span>
                      </td>
                      <td data-label="Joined" className="up-nowrap">
                        {joined || <span className="muted">Unknown</span>}
                      </td>
                      <td data-label="Last active" className="up-nowrap">
                        {lastActive ? (
                          <span title={lastSeenDate ? lastSeenDate.toLocaleString() : undefined}>{lastActive}</span>
                        ) : (
                          <span className="muted">Unknown</span>
                        )}
                      </td>
                      <td className="up-actions-cell">
                        <div className="up-actions">
                          <button
                            type="button"
                            className="btn-secondary btn-sm"
                            onClick={() => openNotificationModal(u)}
                            disabled={isDeleting}
                          >
                            <Notification size={16} variant="Linear" />
                            Notify
                          </button>
                          <button
                            type="button"
                            className="btn-icon up-delete-btn"
                            onClick={() => handleDeleteUser(u)}
                            disabled={isDeleting}
                            aria-label={isDeleting ? `Deleting ${displayName}` : `Delete ${displayName}`}
                            title={isDeleting ? "Deleting" : "Delete user"}
                          >
                            {isDeleting ? <span className="btn-spinner" /> : <Trash size={18} variant="Linear" />}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      {filteredUsers.length > ITEMS_PER_PAGE && (
        <nav className="pagination up-pagination" aria-label="Users pages">
          <span>
            Showing {startIndex + 1} to {endIndex} of {filteredUsers.length} users
          </span>
          <div className="pagination-pages">
            <button
              type="button"
              className="page-btn up-page-arrow"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              aria-label="Previous page"
            >
              <ArrowLeft2 size={14} variant="Linear" />
            </button>
            {pageWindow(currentPage, totalPages).map((page) =>
              typeof page === "string" ? (
                <span key={page} className="up-page-gap" aria-hidden="true">…</span>
              ) : (
                <button
                  key={page}
                  type="button"
                  className={`page-btn ${currentPage === page ? "active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                  aria-current={currentPage === page ? "page" : undefined}
                  aria-label={`Page ${page}`}
                >
                  {page}
                </button>
              )
            )}
            <button
              type="button"
              className="page-btn up-page-arrow"
              disabled={currentPage === totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              aria-label="Next page"
            >
              <ArrowRight2 size={14} variant="Linear" />
            </button>
          </div>
        </nav>
      )}

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal up-notify-modal" role="dialog" aria-modal="true" aria-labelledby="up-notify-title">
            <div className="modal-header">
              <h3 id="up-notify-title">
                Send notification to {selectedUser?.name || selectedUser?.phoneNumber || 'user'}
              </h3>
              <button
                type="button"
                className="btn-icon"
                onClick={closeNotificationModal}
                aria-label="Close"
                disabled={isSending}
              >
                <CloseCircle size={20} variant="Linear" />
              </button>
            </div>
            <form onSubmit={handleSendNotification}>
              <div className="modal-body">
                <div className="field">
                  <label className="field-label" htmlFor="up-notify-title-input">Title</label>
                  <input
                    id="up-notify-title-input"
                    type="text"
                    className="input"
                    placeholder="e.g. Account update"
                    value={notifyTitle}
                    onChange={(e) => setNotifyTitle(e.target.value)}
                    required
                    disabled={isSending}
                  />
                </div>
                <div className="field">
                  <label className="field-label" htmlFor="up-notify-body-input">Message</label>
                  <textarea
                    id="up-notify-body-input"
                    className="textarea"
                    placeholder="What should this user know?"
                    value={notifyBody}
                    onChange={(e) => setNotifyBody(e.target.value)}
                    required
                    rows="4"
                    disabled={isSending}
                  />
                  <span className="field-hint">Sent as a push notification and saved to the user's notifications.</span>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeNotificationModal} disabled={isSending}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={isSending}>
                  {isSending && <span className="btn-spinner" />}
                  {isSending ? "Sending" : "Send notification"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
